import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { tfmErrorMessage } from '../../tfm-review.utils';

export interface ModalAddTfmCommentData {
  courseId: string;
  submissionId: number;
  parentId?: number | null;
  /** 'draft' targets draft submissions; 'final' targets final submissions */
  type?: 'draft' | 'final';
}

@Component({
  selector: 'app-modal-add-tfm-comment',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    EnfafModalComponent
  ],
  templateUrl: './modal-add-tfm-comment.component.html',
  styleUrl: './modal-add-tfm-comment.component.scss'
})
export class ModalAddTfmCommentComponent implements OnInit, OnDestroy {
  form: FormGroup = new FormGroup({});
  isSaving = false;
  showCloseAlert = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalAddTfmCommentData,
    private dialogRef: MatDialogRef<ModalAddTfmCommentComponent>,
    private api: ApiService,
    private toast: ToastService,
    private alert: AlertService
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      content: new FormControl(null, [Validators.required])
    });
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.showCloseAlert = true;
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<void> {
    if (this.showCloseAlert) {
      const close = await this.alert.presentCloseWindowAlert();
      if (close) this.closeModal();
      return;
    }
    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  async saveComment(): Promise<void> {
    if (this.form.invalid) return;
    try {
      this.isSaving = true;
      const body = { content: this.form.value.content.trim(), parentId: this.data.parentId ?? null };
      const call$ =
        this.data.type === 'final'
          ? this.api.addTfmFinalComment(this.data.courseId, this.data.submissionId, body)
          : this.api.addTfmDraftComment(this.data.courseId, this.data.submissionId, body);
      const response = await firstValueFrom(call$);
      this.closeModal({ success: true, comment: response?.data });
      this.toast.presentSuccessToast('Comentario enviado.');
    } catch (error) {
      this.toast.presentErrorToast(tfmErrorMessage(error, 'Error enviando el comentario.'));
    } finally {
      this.isSaving = false;
    }
  }
}
